/**
 * SiteAttendanceTrackerTab
 * Tracks attendance compliance for site personnel (SS, SM, SID/ASID)
 * Shows all active sites with color-coded attendance status
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { siteDatabaseAPI } from '../../../services/api';

// Attendance status display config
const ATTENDANCE_STATUS_CONFIG = {
  on_track: {
    label: 'On Track',
    shortLabel: 'OK',
    color: 'bg-green-100 text-green-800',
    dotColor: 'bg-green-500',
    textColor: 'text-green-600'
  },
  due_soon: {
    label: 'Due Soon',
    shortLabel: 'Soon',
    color: 'bg-yellow-100 text-yellow-800',
    dotColor: 'bg-yellow-500',
    textColor: 'text-yellow-600'
  },
  overdue: {
    label: 'Overdue',
    shortLabel: 'Overdue',
    color: 'bg-red-100 text-red-800',
    dotColor: 'bg-red-500',
    textColor: 'text-red-600'
  },
  never_visited: {
    label: 'Never Visited',
    shortLabel: 'Never',
    color: 'bg-orange-100 text-orange-800',
    dotColor: 'bg-orange-500',
    textColor: 'text-orange-600'
  },
  no_assignment: {
    label: 'Not Assigned',
    shortLabel: 'N/A',
    color: 'bg-gray-100 text-gray-500',
    dotColor: 'bg-gray-400',
    textColor: 'text-gray-400'
  }
};

// Format date for display
function formatLastVisit(lastVisitDate, daysAgo) {
  if (!lastVisitDate) return 'Never visited';

  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${daysAgo} days ago`;
}

// Attendance cell component for table
function AttendanceCell({ name, status, daysAgo, lastVisitDate }) {
  const config = ATTENDANCE_STATUS_CONFIG[status] || ATTENDANCE_STATUS_CONFIG.no_assignment;

  return (
    <div className="text-sm">
      <div className="font-medium text-gray-900 truncate max-w-[120px]">
        {name || 'Not Assigned'}
      </div>
      {name && (
        <div className="flex items-center gap-1.5 mt-1">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${config.dotColor}`} />
          <span className={`text-xs ${config.textColor} ${status === 'overdue' ? 'font-medium' : ''}`}>
            {formatLastVisit(lastVisitDate, daysAgo)}
          </span>
        </div>
      )}
    </div>
  );
}

// Compact attendance cell for mobile
function AttendanceCompact({ label, name, status, daysAgo, lastVisitDate }) {
  const config = ATTENDANCE_STATUS_CONFIG[status] || ATTENDANCE_STATUS_CONFIG.no_assignment;

  return (
    <div className="text-center">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`w-3 h-3 rounded-full mx-auto mb-1 ${config.dotColor}`} />
      {name ? (
        <div className={`text-xs ${config.textColor} font-medium`}>
          {daysAgo !== null ? (daysAgo === 0 ? 'Today' : `${daysAgo}d`) : 'Never'}
        </div>
      ) : (
        <div className="text-xs text-gray-400">N/A</div>
      )}
    </div>
  );
}

export default function SiteAttendanceTrackerTab() {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [summary, setSummary] = useState({ total: 0, onTrack: 0, dueSoon: 0, overdue: 0, neverVisited: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter state
  const [filters, setFilters] = useState({
    search: '',
    statusFilter: '' // on_track, due_soon, overdue, never_visited
  });

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      setLoading(true);
      const response = await siteDatabaseAPI.getAttendanceTracker();
      setSites(response.data.data || []);
      setSummary(response.data.summary || { total: 0, onTrack: 0, dueSoon: 0, overdue: 0, neverVisited: 0 });
      setError(null);
    } catch (err) {
      console.error('Error fetching attendance tracker:', err);
      setError(err.message || 'Failed to load attendance tracker');
    } finally {
      setLoading(false);
    }
  }

  // Filter sites
  const filteredSites = useMemo(() => {
    let result = [...sites];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(s =>
        s.projectNumber?.toLowerCase().includes(searchLower) ||
        s.clientName?.toLowerCase().includes(searchLower) ||
        s.siteSupervisor?.name?.toLowerCase().includes(searchLower) ||
        s.siteManager?.name?.toLowerCase().includes(searchLower) ||
        s.sidAsid?.sidName?.toLowerCase().includes(searchLower) ||
        s.sidAsid?.asidName?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (filters.statusFilter) {
      result = result.filter(s => s.overallStatus === filters.statusFilter);
    }

    return result;
  }, [sites, filters]);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  // Handle status card click
  const handleStatusClick = (status) => {
    setFilters(prev => ({
      ...prev,
      statusFilter: prev.statusFilter === status ? '' : status
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({ search: '', statusFilter: '' });
  };

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.search !== '' || filters.statusFilter !== '';
  }, [filters]);

  // Handle row click - navigate to site detail page
  const handleRowClick = (site) => {
    if (site.id) {
      navigate(`/sites/${site.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-600">{error}</p>
        <button
          onClick={fetchData}
          className="mt-2 text-sm text-red-700 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Requirements Legend - Compact on mobile */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs md:text-sm text-blue-700">
        <div className="font-medium mb-1">Check-in Requirements:</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          <span>SS: every 3 days</span>
          <span>SM: every 7 days</span>
          <span>SID/ASID: every 14 days</span>
        </div>
      </div>

      {/* Stats Summary - Horizontal scroll on mobile, grid on desktop */}
      <div className="flex md:grid md:grid-cols-5 gap-3 mb-6 overflow-x-auto pb-2 md:pb-0 snap-x snap-mandatory md:snap-none scrollbar-hide">
        {/* Total Active Sites */}
        <div className="bg-white rounded-lg border border-gray-200 p-3 md:p-4 flex-shrink-0 w-[140px] md:w-auto snap-start">
          <div className="text-xs md:text-sm font-medium text-gray-500 mb-1">Active Sites</div>
          <div className="text-xl md:text-2xl font-bold text-gray-900">{summary.total}</div>
        </div>

        {/* On Track */}
        <div
          className={`bg-white rounded-lg border-2 p-3 md:p-4 cursor-pointer active:scale-95 md:hover:shadow-md transition-all flex-shrink-0 w-[140px] md:w-auto snap-start ${
            filters.statusFilter === 'on_track' ? 'border-green-500 ring-2 ring-green-200' : 'border-gray-200'
          }`}
          onClick={() => handleStatusClick('on_track')}
        >
          <div className="inline-block px-1.5 md:px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800 mb-2">
            On Track
          </div>
          <div className="text-xl md:text-2xl font-bold text-gray-900">{summary.onTrack}</div>
        </div>

        {/* Due Soon */}
        <div
          className={`bg-white rounded-lg border-2 p-3 md:p-4 cursor-pointer active:scale-95 md:hover:shadow-md transition-all flex-shrink-0 w-[140px] md:w-auto snap-start ${
            filters.statusFilter === 'due_soon' ? 'border-yellow-500 ring-2 ring-yellow-200' : 'border-gray-200'
          }`}
          onClick={() => handleStatusClick('due_soon')}
        >
          <div className="inline-block px-1.5 md:px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800 mb-2">
            Due Soon
          </div>
          <div className="text-xl md:text-2xl font-bold text-gray-900">{summary.dueSoon}</div>
        </div>

        {/* Overdue */}
        <div
          className={`bg-white rounded-lg border-2 p-3 md:p-4 cursor-pointer active:scale-95 md:hover:shadow-md transition-all flex-shrink-0 w-[140px] md:w-auto snap-start ${
            filters.statusFilter === 'overdue' ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-200'
          }`}
          onClick={() => handleStatusClick('overdue')}
        >
          <div className="inline-block px-1.5 md:px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800 mb-2">
            Overdue
          </div>
          <div className="text-xl md:text-2xl font-bold text-gray-900">{summary.overdue}</div>
        </div>

        {/* Never Visited */}
        <div
          className={`bg-white rounded-lg border-2 p-3 md:p-4 cursor-pointer active:scale-95 md:hover:shadow-md transition-all flex-shrink-0 w-[140px] md:w-auto snap-start ${
            filters.statusFilter === 'never_visited' ? 'border-orange-500 ring-2 ring-orange-200' : 'border-gray-200'
          }`}
          onClick={() => handleStatusClick('never_visited')}
        >
          <div className="inline-block px-1.5 md:px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800 mb-2">
            <span className="md:hidden">Never</span>
            <span className="hidden md:inline">Never Visited</span>
          </div>
          <div className="text-xl md:text-2xl font-bold text-gray-900">{summary.neverVisited}</div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="card mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-gray-700">Filters</h3>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear all
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Search */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Search</label>
            <input
              type="text"
              placeholder="Project number, client, personnel..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Attendance Status</label>
            <select
              value={filters.statusFilter}
              onChange={(e) => handleFilterChange('statusFilter', e.target.value)}
              className="w-full px-3 py-2.5 md:py-2 text-base md:text-sm border border-gray-300 rounded-lg md:rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">All Statuses</option>
              <option value="on_track">On Track</option>
              <option value="due_soon">Due Soon</option>
              <option value="overdue">Overdue</option>
              <option value="never_visited">Never Visited</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="mb-4 text-sm text-gray-600">
        Showing {filteredSites.length} of {sites.length} active sites
        {hasActiveFilters && ' (filtered)'}
      </div>

      {/* Empty State */}
      {filteredSites.length === 0 ? (
        <div className="card">
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No active sites</h3>
            <p className="mt-1 text-sm text-gray-500">
              {hasActiveFilters
                ? 'No sites match the current filters'
                : 'Active sites will appear here when available'}
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {filteredSites.map((site) => {
              const overallConfig = ATTENDANCE_STATUS_CONFIG[site.overallStatus] || ATTENDANCE_STATUS_CONFIG.on_track;

              return (
                <div
                  key={site.id}
                  onClick={() => handleRowClick(site)}
                  className="bg-white rounded-lg border border-gray-200 p-4 cursor-pointer active:bg-gray-50 transition-colors"
                >
                  {/* Card Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{site.projectNumber}</div>
                      <div className="text-xs text-gray-500">
                        {site.projectType === 'build_from_land' ? 'Build from Land' : 'Renovation'}
                      </div>
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${overallConfig.color}`}>
                      {overallConfig.shortLabel}
                    </span>
                  </div>

                  {/* Client Name */}
                  <div className="text-sm font-medium text-gray-900 line-clamp-1 mb-3">
                    {site.clientName}
                  </div>

                  {/* Attendance Grid */}
                  <div className="grid grid-cols-3 gap-2 py-3 border-t border-gray-100">
                    <AttendanceCompact
                      label="SS"
                      name={site.siteSupervisor?.name}
                      status={site.siteSupervisor?.status}
                      daysAgo={site.siteSupervisor?.daysAgo}
                      lastVisitDate={site.siteSupervisor?.lastVisitDate}
                    />
                    <AttendanceCompact
                      label="SM"
                      name={site.siteManager?.name}
                      status={site.siteManager?.status}
                      daysAgo={site.siteManager?.daysAgo}
                      lastVisitDate={site.siteManager?.lastVisitDate}
                    />
                    <AttendanceCompact
                      label="SID"
                      name={site.sidAsid?.sidName || site.sidAsid?.asidName}
                      status={site.sidAsid?.status}
                      daysAgo={site.sidAsid?.daysAgo}
                      lastVisitDate={site.sidAsid?.lastVisitDate}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Project
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Site Supervisor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Site Manager
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SID/ASID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Overall
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredSites.map((site) => {
                    const overallConfig = ATTENDANCE_STATUS_CONFIG[site.overallStatus] || ATTENDANCE_STATUS_CONFIG.on_track;

                    return (
                      <tr
                        key={site.id}
                        onClick={() => handleRowClick(site)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{site.projectNumber}</div>
                          <div className="text-xs text-gray-500">
                            {site.projectType === 'build_from_land' ? 'Build' : 'Renovation'}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-gray-900 max-w-[150px] line-clamp-2">
                            {site.clientName}
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <AttendanceCell
                            name={site.siteSupervisor?.name}
                            status={site.siteSupervisor?.status}
                            daysAgo={site.siteSupervisor?.daysAgo}
                            lastVisitDate={site.siteSupervisor?.lastVisitDate}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <AttendanceCell
                            name={site.siteManager?.name}
                            status={site.siteManager?.status}
                            daysAgo={site.siteManager?.daysAgo}
                            lastVisitDate={site.siteManager?.lastVisitDate}
                          />
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm">
                            <div className="font-medium text-gray-900 truncate max-w-[120px]">
                              {site.sidAsid?.sidName || site.sidAsid?.asidName || 'Not Assigned'}
                            </div>
                            {(site.sidAsid?.sidName || site.sidAsid?.asidName) && (
                              <>
                                {site.sidAsid?.sidName && site.sidAsid?.asidName && (
                                  <div className="text-xs text-gray-500 truncate max-w-[120px]">
                                    ASID: {site.sidAsid.asidName}
                                  </div>
                                )}
                                <div className="flex items-center gap-1.5 mt-1">
                                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ATTENDANCE_STATUS_CONFIG[site.sidAsid?.status]?.dotColor || 'bg-gray-400'}`} />
                                  <span className={`text-xs ${ATTENDANCE_STATUS_CONFIG[site.sidAsid?.status]?.textColor || 'text-gray-400'} ${site.sidAsid?.status === 'overdue' ? 'font-medium' : ''}`}>
                                    {formatLastVisit(site.sidAsid?.lastVisitDate, site.sidAsid?.daysAgo)}
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${overallConfig.color}`}>
                            {overallConfig.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
